import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRightLeft, Download, Phone } from "lucide-react";

const MODE = { transfer: ["TRANSFER", "#A78BFA"], mutual_aid: ["MUTUAL-AID", "#22D3EE"], psp_assist: ["PSP ASSIST", "#F59E0B"] };

export default function TransferLog() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [mode, setMode] = useState("");
  const [data, setData] = useState(null);
  const load = () => client.get("/ali/agency-routes", { params: { month, mode } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load transfer log"));
  useEffect(() => { load(); }, [month, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCsv = async () => {
    try {
      const r = await client.get("/ali/agency-routes.csv", { params: { month, mode }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `agency_transfers_${month || "all"}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const months = data?.months?.length ? Array.from(new Set([month, ...data.months])).filter(Boolean) : [month];
  return (
    <Layout>
      <PageHeader title="Outside-Agency Transfer Log" sub="Every transfer & mutual-aid notice · monthly review · CSV export" icon={ArrowRightLeft} />
      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="transfer-log">
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="transfer-month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono text-white">
            <option value="">All months</option>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {["", "transfer", "mutual_aid", "psp_assist"].map((m) => (
            <button key={m} data-testid={`transfer-mode-${m || "all"}`} onClick={() => setMode(m)}
              className={`px-2.5 py-1.5 text-[10px] font-head font-bold border ${mode === m ? "bg-[#3B82F6] border-[#3B82F6] text-white" : "border-[#2C2D35] text-[#9CA3AF]"}`}>{m === "" ? "ALL" : MODE[m][0]}</button>
          ))}
          <button data-testid="transfer-export-csv" onClick={exportCsv} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-head font-bold border border-[#10B981]/60 text-[#10B981] hover:bg-[#10B981]/15"><Download size={12} /> EXPORT CSV</button>
        </div>
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["TOTAL", data.total, "#fff"], ["TRANSFERS", data.by_mode.transfer || 0, "#A78BFA"], ["MUTUAL-AID", data.by_mode.mutual_aid || 0, "#22D3EE"], ["AGENCIES", data.by_agency.length, "#F59E0B"]].map(([l, v, c]) => (
              <div key={l} className="border border-[#2C2D35] bg-[#141518] p-3"><div className="font-mono text-[9px] uppercase text-[#6B7280]">{l}</div><div className="font-head font-bold text-2xl" style={{ color: c }} data-testid={`transfer-stat-${l.toLowerCase()}`}>{v}</div></div>
            ))}
          </div>
        )}
        {data?.by_agency?.length > 0 && (
          <div className="border border-[#2C2D35] bg-[#141518] p-3">
            <div className="font-mono text-[9px] uppercase text-[#6B7280] mb-1.5">By receiving agency</div>
            <div className="flex flex-wrap gap-1.5">{data.by_agency.map(([a, n]) => <span key={a} className="px-2 py-0.5 border border-[#2C2D35] font-mono text-[10px] text-[#E5E7EB]">{a} <span className="text-[#F59E0B] font-bold">{n}</span></span>)}</div>
          </div>
        )}
        <div className="border border-[#2C2D35] bg-[#141518] overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="font-mono text-[9px] uppercase text-[#6B7280] border-b border-[#2C2D35]">
              <tr>{["Time", "Type", "Agency / PSAP", "Location", "Caller", "Call type", "CC#", "By"].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={i} data-testid={`transfer-row-${i}`} className="border-b border-[#1f2026] hover:bg-[#1c1d22]">
                  <td className="px-2 py-1.5 font-mono text-[10px] text-[#9CA3AF] whitespace-nowrap">{r.ts.slice(0, 10)} {r.ts.slice(11, 16)}Z</td>
                  <td className="px-2"><span className="px-1.5 py-0.5 font-mono text-[9px] font-bold border" style={{ color: MODE[r.mode]?.[1], borderColor: MODE[r.mode]?.[1] }}>{MODE[r.mode]?.[0] || r.mode}</span></td>
                  <td className="px-2 text-white">{r.agency}{r.psap_name && <div className="font-mono text-[10px] text-[#9CA3AF] flex items-center gap-1">{r.psap_name}{r.psap_phone && <><Phone size={9} /> {r.psap_phone}</>}</div>}</td>
                  <td className="px-2 font-mono text-[10px] text-[#D1D5DB]">{r.common_name ? <span className="text-[#FCD34D]">{r.common_name} · </span> : null}{r.address || "—"}</td>
                  <td className="px-2 font-mono text-[10px] text-[#D1D5DB]">{r.caller?.name || "—"}{r.caller?.phone ? <div className="text-[#9CA3AF]">{r.caller.phone}</div> : null}</td>
                  <td className="px-2 text-[#D1D5DB]">{r.call_type || "—"}</td>
                  <td className="px-2 font-mono text-[10px] text-[#93C5FD]">{r.cc_number || "—"}</td>
                  <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{r.by}</td>
                </tr>
              ))}
              {data && !data.rows.length && <tr><td colSpan={8} className="px-2 py-4 text-center font-mono text-[10px] text-[#6B7280]">No transfers or mutual-aid notices for this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
